import Link from 'next/link';
import styled from 'styled-components';

import theme from '../assets/theme';

const aiPrinciples = [
  {
    title: 'Create lessons that up-skill teachers',
    eyebrow: 'Curriculum expert led',
    body:
      'Curipod lesson structures are built by curriculum experts around research-based practices like gradual release, scaffolding, formative assessment, and the feedback loop. AI helps fill in lesson content inside that human-designed structure.',
    result:
      'Teachers get student-centered lessons where students read, write, discuss, and connect with each other.',
  },
  {
    title: 'Close the feedback loop every time',
    eyebrow: 'Rubric aligned',
    body:
      'AI feedback is structured around rigorous rubrics and teacher-designed learning goals. Students receive one exchange at a time, so AI never becomes an unsupervised back-and-forth chatbot.',
    result:
      'Teachers can monitor, edit, and use immediate feedback to help students reflect, discuss, and try again in the same class period.',
  },
  {
    title: 'Provide actionable insights in a click',
    eyebrow: 'Teacher ready reports',
    body:
      'Curipod synthesizes participation and performance into reports that show what is going well, where students need support, and what teachers can do next.',
    result:
      'Teachers get time back while making more informed decisions for the class and individual students.',
  },
  {
    title: 'Keep teachers in control',
    eyebrow: 'Not student-facing AI',
    body:
      'Every AI interaction in Curipod is teacher initiated, structured into a lesson, and limited in scope. Students do not use AI independently in Curipod.',
    result:
      'AI supports teacher judgment instead of replacing it, and teachers remain in control of the learning experience.',
  },
  {
    title: 'Safeguard student privacy',
    eyebrow: 'Privacy by design',
    body:
      'We never use teacher or student data to train the AI models we use. Teacher and student personal data is not shared with model providers.',
    result:
      'Curipod is built for schools with COPPA, FERPA, and GDPR compliance at the center.',
  },
];

const modelGroups = [
  {
    provider: 'OpenAI',
    models: ['o3', 'gpt-5', 'gpt-4o', 'gpt-4o-mini', 'gpt-image'],
  },
  {
    provider: 'Google Gemini',
    models: ['gemini-3-pro', 'gemini-3-flash'],
  },
  {
    provider: 'Ideogram',
    models: ['ideogram-v3-turbo'],
  },
  {
    provider: 'Stable Diffusion',
    models: ['stable-diffusion-large'],
  },
];

const trustPoints = [
  'No independent student AI chatbot',
  'Teacher-initiated AI interactions',
  'Teacher review and edit controls',
  'No training on teacher or student data',
  'COPPA, FERPA, and GDPR aligned',
];

const SafeAiInCuripodPage = () => {
  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <HeroEyebrow>SAFE AI IN CURIPOD</HeroEyebrow>
          <HeroTitle>AI that supports teachers, not replaces them.</HeroTitle>
          <HeroLead>
            We use AI when it helps solve real classroom problems: student engagement,
            literacy, critical thinking, equity gaps, and keeping great teachers in the
            classroom.
          </HeroLead>
          <HeroActions>
            <Link href="/quotes" passHref prefetch={false}>
              <PrimaryLink>Contact us about the School Plan</PrimaryLink>
            </Link>
            <Link href="/learning-principles" passHref>
              <SecondaryLink>Read our learning principles</SecondaryLink>
            </Link>
            <Link href="/privacy-policy" passHref>
              <SecondaryLink>Read our privacy policy</SecondaryLink>
            </Link>
          </HeroActions>
        </HeroContent>
        <HeroCard aria-label="Curipod safe AI guardrails">
          {trustPoints.map(point => (
            <TrustPill key={point}>{point}</TrustPill>
          ))}
        </HeroCard>
      </HeroSection>

      <PrinciplesSection>
        <SectionHeader>
          <SectionEyebrow>Our AI principles</SectionEyebrow>
          <SectionTitle>Designed around the classroom, not the model.</SectionTitle>
          <SectionLead>
            Curipod starts with the teacher-led learning experience. AI is a tool inside
            that experience, with guardrails that keep teachers in control and students
            protected.
          </SectionLead>
        </SectionHeader>
        <PrinciplesGrid>
          {aiPrinciples.map((principle, index) => (
            <PrincipleCard key={principle.title}>
              <CardNumber>{String(index + 1).padStart(2, '0')}</CardNumber>
              <CardEyebrow>{principle.eyebrow}</CardEyebrow>
              <CardTitle>{principle.title}</CardTitle>
              <CardBody>{principle.body}</CardBody>
              <CardResult>{principle.result}</CardResult>
            </PrincipleCard>
          ))}
        </PrinciplesGrid>
      </PrinciplesSection>

      <TeacherControlSection>
        <TeacherControlContent>
          <SectionEyebrow>Teacher controlled</SectionEyebrow>
          <DarkSectionTitle>Students never sit alone with AI.</DarkSectionTitle>
          <DarkSectionText>
            Curipod is not a student-facing AI chatbot. AI interactions are embedded in
            teacher-led lessons, happen one exchange at a time, and are visible to the
            teacher.
          </DarkSectionText>
        </TeacherControlContent>
        <ControlList>
          <ControlItem>Teacher initiates the AI-powered activity.</ControlItem>
          <ControlItem>Student interaction is structured by the lesson.</ControlItem>
          <ControlItem>Teachers can monitor and edit AI feedback.</ControlItem>
          <ControlItem>
            Students return to discussion, reflection, and revision.
          </ControlItem>
        </ControlList>
      </TeacherControlSection>

      <ModelsSection>
        <SectionHeader>
          <SectionEyebrow>Transparency</SectionEyebrow>
          <SectionTitle>Foundation models we use</SectionTitle>
          <SectionLead>
            We are transparent about the model families Curipod uses and update this list
            as better models become available.
          </SectionLead>
        </SectionHeader>
        <ModelsGrid>
          {modelGroups.map(group => (
            <ModelGroupCard key={group.provider}>
              <ModelProvider>{group.provider}</ModelProvider>
              <ModelList>
                {group.models.map(model => (
                  <ModelChip key={model}>{model}</ModelChip>
                ))}
              </ModelList>
            </ModelGroupCard>
          ))}
        </ModelsGrid>
      </ModelsSection>

      <BottomCtaSection>
        <BottomCtaEyebrow>Bring safe AI to your school</BottomCtaEyebrow>
        <BottomCtaTitle>Want to learn more about the School Plan?</BottomCtaTitle>
        <BottomCtaText>
          Talk with us about how Curipod supports teacher-controlled AI, privacy, and
          implementation across your school or district.
        </BottomCtaText>
        <Link href="/quotes" passHref prefetch={false}>
          <BottomCtaLink>Contact us about the School Plan</BottomCtaLink>
        </Link>
      </BottomCtaSection>
    </Container>
  );
};

const MOBILE_QUERY = '(max-width: 900px)';

const Container = styled.div`
  width: 100%;
  background: ${theme.colors.landingPageBeige};
  color: ${theme.colors.black};
`;

const HeroSection = styled.section`
  width: 100%;
  min-height: calc(100dvh - 60px);
  padding: 120px 24px 90px;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: 56px;
  align-items: center;
  max-width: 1280px;
  margin: 0 auto;
  box-sizing: border-box;

  @media ${MOBILE_QUERY} {
    min-height: auto;
    grid-template-columns: 1fr;
    padding: 88px 20px 64px;
    gap: 32px;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const HeroEyebrow = styled.p`
  margin: 0 0 18px;
  color: ${theme.colors.green};
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 2.8px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  max-width: 860px;
  font-size: clamp(46px, 7vw, 86px);
  line-height: 0.95;
  font-weight: 900;
  letter-spacing: -0.06em;
`;

const HeroLead = styled.p`
  margin: 28px 0 0;
  max-width: 720px;
  font-size: 23px;
  line-height: 1.45;
  font-weight: 700;

  @media ${MOBILE_QUERY} {
    font-size: 18px;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 36px;
`;

const BaseLink = styled.a`
  min-height: 52px;
  padding: 0 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 900;
  text-decoration: none;
  transition: ${theme.effects.defaultTransition};

  &:hover {
    transform: translateY(-2px);
  }
`;

const PrimaryLink = styled(BaseLink)`
  background: ${theme.colors.orange};
  color: ${theme.colors.black};
  box-shadow: 0 14px 28px rgba(249, 151, 70, 0.26);
`;

const SecondaryLink = styled(BaseLink)`
  border: 2px solid rgba(40, 47, 51, 0.18);
  color: ${theme.colors.black};
  background: rgba(255, 255, 255, 0.62);
`;

const HeroCard = styled.div`
  position: relative;
  border-radius: 32px;
  padding: 32px;
  background: linear-gradient(135deg, ${theme.colors.black} 0%, #1a1a2e 100%);
  box-shadow: 0 28px 70px rgba(40, 47, 51, 0.22);
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 240px;
    height: 240px;
    top: -80px;
    right: -60px;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.18);
  }
`;

const TrustPill = styled.div`
  position: relative;
  z-index: 1;
  padding: 18px 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  color: ${theme.colors.white};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const PrinciplesSection = styled.section`
  padding: 96px 24px;
  background: ${theme.colors.white};
`;

const SectionHeader = styled.div`
  max-width: 860px;
  margin: 0 auto 48px;
  text-align: center;
`;

const SectionEyebrow = styled.p`
  margin: 0 0 12px;
  color: ${theme.colors.green};
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 2.6px;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(34px, 5vw, 56px);
  line-height: 1.02;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const SectionLead = styled.p`
  margin: 20px auto 0;
  max-width: 760px;
  font-size: 20px;
  line-height: 1.55;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.78);

  @media ${MOBILE_QUERY} {
    font-size: 17px;
  }
`;

const PrinciplesGrid = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media ${MOBILE_QUERY} {
    grid-template-columns: 1fr;
  }
`;

const PrincipleCard = styled.article`
  padding: 30px;
  border-radius: 28px;
  background: ${theme.colors.landingPageBeige};
  border: 1px solid rgba(40, 47, 51, 0.08);
  box-shadow: 0 18px 40px rgba(40, 47, 51, 0.07);
`;

const CardNumber = styled.div`
  color: rgba(249, 151, 70, 0.95);
  font-size: 34px;
  font-weight: 900;
  line-height: 1;
`;

const CardEyebrow = styled.p`
  margin: 18px 0 8px;
  color: ${theme.colors.green};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 26px;
  line-height: 1.15;
  font-weight: 900;
`;

const CardBody = styled.p`
  margin: 16px 0 0;
  font-size: 17px;
  line-height: 1.6;
  color: rgba(40, 47, 51, 0.78);
`;

const CardResult = styled.p`
  margin: 18px 0 0;
  padding-left: 16px;
  border-left: 4px solid ${theme.colors.orange};
  font-size: 16px;
  line-height: 1.5;
  font-weight: 800;
`;

const TeacherControlSection = styled.section`
  padding: 96px 24px;
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.9fr);
  gap: 56px;
  align-items: center;

  @media ${MOBILE_QUERY} {
    grid-template-columns: 1fr;
    padding: 72px 20px;
    gap: 32px;
  }
`;

const TeacherControlContent = styled.div`
  max-width: 600px;
  justify-self: end;

  @media ${MOBILE_QUERY} {
    justify-self: start;
  }
`;

const DarkSectionTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.white};
  font-size: clamp(34px, 5vw, 56px);
  line-height: 1.03;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const DarkSectionText = styled.p`
  margin: 22px 0 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 20px;
  line-height: 1.6;
  font-weight: 700;
`;

const ControlList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ControlItem = styled.li`
  padding: 20px 22px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  color: ${theme.colors.white};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.35;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const ModelsSection = styled.section`
  padding: 96px 24px 110px;
  background: ${theme.colors.background};
`;

const ModelsGrid = styled.div`
  max-width: 980px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media ${MOBILE_QUERY} {
    grid-template-columns: 1fr;
  }
`;

const ModelGroupCard = styled.article`
  padding: 28px;
  border-radius: 26px;
  background: ${theme.colors.white};
  border: 1px solid rgba(40, 47, 51, 0.08);
  box-shadow: 0 18px 40px rgba(40, 47, 51, 0.07);
`;

const ModelProvider = styled.h3`
  margin: 0 0 18px;
  font-size: 24px;
  font-weight: 900;
`;

const ModelList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ModelChip = styled.span`
  padding: 10px 14px;
  border-radius: 999px;
  background: ${theme.colors.landingPageBeige};
  color: ${theme.colors.black};
  font-size: 14px;
  font-weight: 900;
  border: 1px solid rgba(40, 47, 51, 0.08);
`;

const BottomCtaSection = styled.section`
  padding: 96px 24px;
  background: ${theme.colors.landingPageBeige};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const BottomCtaEyebrow = styled.p`
  margin: 0 0 12px;
  color: ${theme.colors.green};
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 2.6px;
  text-transform: uppercase;
`;

const BottomCtaTitle = styled.h2`
  margin: 0;
  max-width: 760px;
  font-size: clamp(34px, 5vw, 56px);
  line-height: 1.02;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const BottomCtaText = styled.p`
  margin: 20px auto 0;
  max-width: 680px;
  font-size: 20px;
  line-height: 1.55;
  font-weight: 700;
  color: rgba(40, 47, 51, 0.78);

  @media ${MOBILE_QUERY} {
    font-size: 17px;
  }
`;

const BottomCtaLink = styled(PrimaryLink)`
  margin-top: 32px;
`;

export default SafeAiInCuripodPage;
